import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { conseguirGrupos, conseguirAniosDisponibles } from '../api/Groups';

type Group = { id: number; name: string; created_at?: string };

type GroupsFilterContextValue = {
    years: number[];
    selectedYear: number | null;
    setSelectedYear: (y: number) => void;
    groups: Group[];
    error: string | null;
    loading: boolean;
};

const GroupsFilterContext = createContext<GroupsFilterContextValue>({
    years: [],
    selectedYear: null,
    setSelectedYear: () => {},
    groups: [],
    error: null,
    loading: false,
});

export const GroupsFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [years, setYears] = useState<number[]>([]);
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [groups, setGroups] = useState<Group[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const data = await conseguirAniosDisponibles();
                const ys: number[] = data?.years || [];
                setYears(ys);
                if (ys.length > 0) setSelectedYear(ys[0]);
            } catch (err: any) {
                console.error('[GroupsFilter] error fetching years:', err);
            }
        })();
    }, []);

    useEffect(() => {
        if (selectedYear === null) return;
        (async () => {
            setLoading(true);
            try {
                const x = await conseguirGrupos(selectedYear);
                if (!x?.results) throw new Error('invalid_response');
                setGroups(x.results);
                setError(null);
            } catch (err: any) {
                const httpStatus = err?.response?.status;
                setError(httpStatus ? `error_status:${httpStatus}` : 'error_connection');
                console.error('[GroupsFilter] error fetching groups:', err);
            } finally {
                setLoading(false);
            }
        })();
    }, [selectedYear]);

    const value = useMemo(() => ({
        years, selectedYear, setSelectedYear, groups, error, loading,
    }), [years, selectedYear, groups, error, loading]);

    return <GroupsFilterContext.Provider value={value}>{children}</GroupsFilterContext.Provider>;
};

export const useGroupsFilter = () => useContext(GroupsFilterContext);
